/**
 * RbAiRuns Repository
 * 
 * Phase E7: AI Run Audit Logging
 * Immutable audit trail for reconstructability
 */

import { 
  Firestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  setDoc,
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import type { RbAiRun, CreateAiRunInput, AiRunStatus } from './types/rb.js';

const COLLECTION = 'rb_aiRuns';

/**
 * Create a new AI run
 * All fields required
 */
export async function createAiRun(
  db: Firestore,
  aiRunId: string,
  input: CreateAiRunInput
): Promise<void> {
  const aiRun: RbAiRun = {
    id: aiRunId,
    workspaceId: input.workspaceId,
    projectId: input.projectId,
    questionId: input.questionId,
    contractId: input.contractId,
    contractVersion: input.contractVersion,
    model: input.model,
    temperature: input.temperature,
    topP: input.topP,
    presencePenalty: input.presencePenalty,
    frequencyPenalty: input.frequencyPenalty,
    buildSha: input.buildSha,
    contractTemplateHash: input.contractTemplateHash,
    retrievalSnapshotHash: input.retrievalSnapshotHash,
    retrievedChunkIds: input.retrievedChunkIds,
    retrievedChunkHashes: input.retrievedChunkHashes,
    minChunks: input.minChunks,
    minDistinctDocs: input.minDistinctDocs,
    maxChunks: input.maxChunks,
    thresholdsPassed: input.thresholdsPassed,
    tone: input.tone,
    targetLength: input.targetLength,
    intendedOutcome: input.intendedOutcome,
    policyInclusion: input.policyInclusion,
    status: input.status,
    errorMessage: input.errorMessage,
    startedAt: Timestamp.now(),
    completedAt: undefined,
    createdByUserId: input.createdByUserId,
  };

  const docRef = doc(db, COLLECTION, aiRunId);
  await setDoc(docRef, aiRun);
}

/**
 * Update AI run completion status
 */
export async function completeAiRun(
  db: Firestore,
  aiRunId: string,
  status: AiRunStatus,
  errorMessage?: string
): Promise<void> {
  const docRef = doc(db, COLLECTION, aiRunId);
  await updateDoc(docRef, {
    status,
    errorMessage: errorMessage || null,
    completedAt: Timestamp.now(),
  });
}

/**
 * Get AI run by ID
 */
export async function getAiRun(
  db: Firestore,
  aiRunId: string
): Promise<RbAiRun | null> {
  const docRef = doc(db, COLLECTION, aiRunId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return docSnap.data() as RbAiRun;
}

/**
 * List AI runs for a question (ordered by creation time, newest first)
 */
export async function listAiRunsForQuestion(
  db: Firestore,
  questionId: string
): Promise<RbAiRun[]> {
  const q = query(
    collection(db, COLLECTION),
    where('questionId', '==', questionId),
    orderBy('startedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as RbAiRun);
}

/**
 * List AI runs for a workspace (for status page)
 */
export async function listAiRunsForWorkspace(
  db: Firestore,
  workspaceId: string
): Promise<RbAiRun[]> {
  const q = query(
    collection(db, COLLECTION),
    where('workspaceId', '==', workspaceId),
    orderBy('startedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => doc.data() as RbAiRun);
}

/**
 * Count AI runs by status for a workspace
 */
export async function countAiRunsByStatus(
  db: Firestore,
  workspaceId: string,
  status: AiRunStatus
): Promise<number> {
  const q = query(
    collection(db, COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('status', '==', status)
  );
  
  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Get last AI run timestamp for a workspace
 */
export async function getLastAiRunTimestamp(
  db: Firestore,
  workspaceId: string
): Promise<Timestamp | null> {
  const q = query(
    collection(db, COLLECTION),
    where('workspaceId', '==', workspaceId),
    orderBy('startedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const lastRun = snapshot.docs[0].data() as RbAiRun;
  return lastRun.startedAt;
}

/**
 * Get last AI error timestamp for a workspace
 */
export async function getLastAiErrorTimestamp(
  db: Firestore,
  workspaceId: string
): Promise<Timestamp | null> {
  const q = query(
    collection(db, COLLECTION),
    where('workspaceId', '==', workspaceId),
    where('status', 'in', ['failed', 'failed_validation']),
    orderBy('startedAt', 'desc')
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const lastError = snapshot.docs[0].data() as RbAiRun;
  return lastError.startedAt;
}
